import React, { useEffect } from 'react';
import { useNavigation } from '../../components/hocs/withNavigation';
import { FooterSpecs, useFooter } from '../../states/FooterState';
import { NavigationSpecs } from '../../states/NavigationState';
import { restrictAccessForUser } from '../../../commonv3/hooks/restrictAccessForUser';
import { CoachingPageState } from './states/CoachingPageState';
import { ToolTip } from '../../components/atoms/tooltips/ToolTip';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import {
  V3Routes,
  NOTION_COACHING_GUIDE_URL,
  NOTION_LIST_OF_COACH_URL,
  NOTION_1_ON_1_COACHING_URL,
} from '../../routes/index';
import { routingStore, needle } from '../../../common/stores/routing';
import { PageForMyPageRenewal } from '../../components/atoms/applications/page';
import { PageHeader } from '../../components/atoms/layout/PageHeader';
import { OverlayPage } from '../../../commonv3/components/molecules/layout/OverlayPage';
import { CoachingSessionListPane } from '../../components/organisms/sessions/CoachingSessionListPane';
import { observer, useLocalStore, Provider } from 'mobx-react';
import { Route, Switch } from 'react-router';
import { SessionReservationPane } from '../../components/organisms/sessions/SessionReservationPane';
import { SessionReservationCompletePane } from '../../components/organisms/sessions/SessionReservationCompletePane';
import { SquareButton } from '../../../commonv3/components/atoms/buttons/SquareButton';
import { CareerCoachingSessionListPane } from '../../components/organisms/sessions/CareerCoachingSessionListPane';
import { AboutSection } from '../../components/atoms/sections/AboutSection';
import Image from './images/about-coaching-section-image.png';
import BenefitsOfAttendingCoachingImage from './images/benefits-of-attending-coaching-image.png';
import TypeOfCoachingImage from './images/type-of-coaching-image.png';
import CoachingGuideImage from './images/coaching-guide-image.png';
import ListOfCoachImage from './images/list-of-coach-image.png';
import OneOnOneCoachingImage from './images/1_on_1_coaching_image.png';
import { PageSection } from '../../components/atoms/sections/PageSection';
import { PageSectionContent } from '../../components/atoms/sections/PageSectionContent';
import { RouterStore } from '../../../common/mobx-react-router';
import { BreadCrumb } from '../breadcrumb/BreadCrumb';
import './coachingPage.module.scss';

type ReservationSectionProps = {
  closeTooltip: () => void;
  coachingReservationClick: () => void;
  coachingTooltipClosed: boolean;
  loading: boolean;
  isEffectiveMultiCreatorUser: boolean;
  routing: RouterStore;
};

const ReservationSection = ({
  closeTooltip,
  coachingReservationClick,
  coachingTooltipClosed,
  loading,
  isEffectiveMultiCreatorUser,
  routing,
}: ReservationSectionProps) => {
  return (
    <div styleName="reservation-section">
      <ToolTip
        id="coaching-tooltip"
        title="まずはコーチングを予約しよう"
        description="継続して学習効率を上げるために、目標設定を行いましょう。"
        onClose={() => closeTooltip()}
        opened={!coachingTooltipClosed}
      >
        <div styleName="button-center">
          <SquareButton
            id={'coaching-reserve-button'}
            loading={loading}
            onReservationClick={() => coachingReservationClick()}
            label="コーチングを予約する"
            buttonWidth={GlobalStatesV3.viewport.isSp ? '100%' : '310px'}
          />
        </div>
      </ToolTip>
      {isEffectiveMultiCreatorUser && (
        <>
          <div styleName="career-coaching-reserve-description">
            マルチクリエイターコースの1on1キャリアコーチング予約はこちらから
          </div>
          <div styleName="button-center">
            <SquareButton
              id={'career-coaching-reserve-button'}
              loading={loading}
              type="multicreator"
              onReservationClick={() => routing.push(V3Routes.careerCoachingSessions2())}
              label="1on1コーチングを予約する"
              buttonWidth={GlobalStatesV3.viewport.isSp ? '100%' : '310px'}
            />
          </div>
        </>
      )}
    </div>
  );
};

const CoachingTypeDescription = () => {
  return (
    <>
      <div styleName="coaching-type-description-block">
        <div styleName="coaching-type-description-title">Welcomeワークショップ</div>
        <div>入会後、最初に参加していただくコーチングです。ありたい未来の姿を一緒に描きゴールを設定しましょう。</div>
      </div>
      <div styleName="coaching-type-description-block">
        <div styleName="coaching-type-description-title">グループコーチング</div>
        <div>
          定期的な振り返りと軌道修正の場で、月に一回の参加がおすすめです。目標を達成できていなくても安心して参加してくださいね。
        </div>
      </div>
      <div>
        <div styleName="coaching-type-description-title">おかえりグループコーチング</div>
        <div>
          最後のコーチングの参加からしばらく（3ヶ月以上）経ってしまった方向けに、再スタートをすることを主な目的にした場です。「グループコーチングに参加するのはハードルが高いな」という方におすすめです。
        </div>
      </div>
    </>
  );
};

const MoreUseContent = () => {
  return (
    <>
      <div styleName="more-use-content-images" data-testid="more-section">
        <a
          href={V3Routes.home(NOTION_COACHING_GUIDE_URL).stringify()}
          target="_blank"
          data-testid="coaching-guide-banner"
        >
          <img styleName="more-use-content-image left-image" src={CoachingGuideImage} />
        </a>
        <a
          href={V3Routes.home(NOTION_LIST_OF_COACH_URL).stringify()}
          target="_blank"
          data-testid="list-of-coach-banner"
        >
          <img styleName="more-use-content-image" src={ListOfCoachImage} />
        </a>
      </div>
      <a
        href={V3Routes.home(NOTION_1_ON_1_COACHING_URL).stringify()}
        target="_blank"
        data-testid="1on1-coaching-banner"
      >
        <img styleName="more-use-content-image" src={OneOnOneCoachingImage} />
      </a>
    </>
  );
};

export const CoachingPage = observer(() => {
  restrictAccessForUser();
  useNavigation(NavigationSpecs.side());
  useFooter(FooterSpecs.likes());

  const { pageState } = useLocalStore(() => {
    return {
      pageState: new CoachingPageState(),
    };
  });
  useEffect(() => {
    pageState.init();
  }, []);

  const { closeCoachingTooltip, loading } = pageState;
  const { currentUser, routing } = GlobalStatesV3;
  const { coachingTooltipClosed } = currentUser.preference;

  const closeTooltip = () => {
    closeCoachingTooltip();
    routing.push(V3Routes.coaching());
  };
  const coachingReservationClick = () => {
    closeCoachingTooltip();
    routing.push(V3Routes.coachingSessions2());
  };

  return (
    <>
      <PageForMyPageRenewal id="coaching-page">
        <div styleName="coaching-page-header">
          <BreadCrumb items={[{ text: 'コーチング ' }]} hasHome />
          <PageHeader title="COACHING" testId="page-header-of-coaching" />
        </div>
        <div styleName="coaching-page-content">
          <AboutSection
            title="コーチングとは"
            description="SHElikesにおけるコーチングとは、自分の夢や理想に近づくために、心からワクワクするゴールを見つけ、設定するための時間です。「自分ならゴールを達成できる」と感じている状態をエフィカシー（自己効力感）が高い状態といい、エフィカシーを高めることもコーチングの大切な役割です。"
            image={Image}
            color="blue"
          />
          <ReservationSection
            closeTooltip={closeTooltip}
            coachingReservationClick={coachingReservationClick}
            coachingTooltipClosed={coachingTooltipClosed}
            loading={loading}
            isEffectiveMultiCreatorUser={currentUser.isEffectiveMultiCreatorUser()}
            routing={routing}
          />
          <div styleName="coaching-page-sections">
            <PageSection title="コーチングに参加するメリット" testid="benefits-section">
              <PageSectionContent
                image={BenefitsOfAttendingCoachingImage}
                body="SHElikesのコーチングに参加すると、定期的な目標の確認・振り返りができ、自分が描いたゴールに到達するスピードが上がります。自分一人で考え込むよりも、思いや感情を言葉にしコーチやシーメイトさんと対話することで視野が広がり、新たな気づきを得られます。学習の進め方や自分自身の感情に目を向ける時間でもあるので、宣言していた目標を達成していなくても大丈夫。コーチングに参加して自分らしい学習計画を立て、充実した時間を過ごしましょう！"
              />
            </PageSection>
            <PageSection title="コーチングの種類" testid="type-section">
              <PageSectionContent image={TypeOfCoachingImage} body={<CoachingTypeDescription />} />
            </PageSection>
            {currentUser.hasEffectiveMembershipToken() && (
              <PageSection title="コーチングをもっと活用しよう">
                <PageSectionContent body={<MoreUseContent />} />
              </PageSection>
            )}
          </div>
        </div>
      </PageForMyPageRenewal>

      <Provider homeState={pageState}>
        <OverlayPage
          open={needle(['sessions'], routingStore.location) !== undefined}
          onRequestClose={() => routingStore.push(V3Routes.coaching())}
        >
          <Switch>
            <Route exact path={'/coaching/sessions'} component={CoachingSessionListPane} />
            <Route exact path={'/coaching/sessions/:sessionId/reservation'} component={SessionReservationPane} />
            <Route
              exact
              path={'/coaching/sessions/:sessionId/reservation_complete'}
              component={SessionReservationCompletePane}
            />
            <Route exact path={'/coaching/career_coaching_sessions'} component={CareerCoachingSessionListPane} />
          </Switch>
        </OverlayPage>
      </Provider>
    </>
  );
});
